import {useLazyQuery, useQuery} from '@apollo/client';
import styles from './orderBook.module.scss';
import {Form, Select, Table, Button} from 'antd';
import {GET_ALL_STOCKS, GET_PLATFORM_PORTFOLIO} from 'graphql/queries/stocks';
import {useEffect, useState} from 'react';
import {GET_ASSET_CLASS_INSTRUMENTS} from 'graphql/queries/assetManagement';
import {toast} from 'react-toastify';
import {FinancialInstrumentPicker} from 'components/financialInstrumentPicker';
import {
  ORDER_INSTRUMENT_KEYS,
  instrumentKeyLabel,
  instrumentKeyOptions,
} from 'pages/AssetManagement/modules/instrumentKeys';
import _ from 'lodash';
import {stockOrderStatus} from 'utils/mock';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {DownloadOutlined} from '@ant-design/icons';
import {ModalShell} from './ui/ModalShell';

// Equity-side portfolios are looked up through the Instrument v2 picker;
// companies are no longer a dimension anywhere in the order book. Bonds and
// mutual funds keep their own asset-class lookup, which lists instruments in
// every status — a disabled one can still hold platform units.
const PICKER_KEYS = ['PUBLIC_EQUITY', 'PRIVATE_EQUITY', 'VENTURE_FUND'];

export const PlatformPortfolio = ({form, open, scopedExchangeId}) => {
  const instrumentKey = Form.useWatch('instrumentKey', form);
  const [instruments, setInstruments] = useState([]);
  const [platformPortfolio, setPlatformPortfolio] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);

  const [getPlatformPortfolio, {loading: loadingPlatformPortfolio}] =
    useLazyQuery(GET_PLATFORM_PORTFOLIO, {
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      onError: error => toast.error(error.message),
    });

  const [
    fetchInstruments,
    {refetch: refetchInstrument, loading: loadingInstruments},
  ] = useLazyQuery(GET_ASSET_CLASS_INSTRUMENTS);

  const {data: stockExchanges, loading: loadingExchanges} = useQuery(
    GET_ALL_STOCKS,
    {skip: !open, errorPolicy: 'all'},
  );

  // The orders page is already looking at one venue; open on that same venue
  // rather than silently reporting the platform's holdings everywhere. It is
  // clearable, so a cross-venue total is still one click away.
  useEffect(() => {
    if (open && scopedExchangeId) {
      form.setFieldValue('stockExchange', scopedExchangeId);
    }
  }, [open, scopedExchangeId]);

  // Both branches now write to `instrumentId`, so a stale id from the previous
  // instrument type would otherwise be submitted against the new one.
  useEffect(() => {
    form.setFieldValue('instrumentId', undefined);
  }, [instrumentKey]);

  useEffect(() => {
    if (['BOND', 'MUTUAL_FUND'].includes(instrumentKey)) {
      fetchInstruments({
        variables: {
          input: {
            explore: false,
            instrumentKey,
            assetClassKey: instrumentKey === 'BOND' ? 'FIXED_INCOMES' : 'FUNDS',
          },
        },
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
        notifyOnNetworkStatusChange: true,
        onError: error => toast.error(error.message),
        onCompleted: data =>
          setInstruments(data?.getFinancialInstruments?.data || []),
      });
    }
  }, [instrumentKey]);

  const handleSearchInstrument = _.debounce(async value => {
    try {
      const {data} = await refetchInstrument({
        input: {
          explore: false,
          instrumentKey,
          ...(['BOND', 'MUTUAL_FUND'].includes(instrumentKey) && {
            assetClassKey: instrumentKey === 'BOND' ? 'FIXED_INCOMES' : 'FUNDS',
          }),
          search: value,
          page: 1,
          limit: 100,
        },
      });
      setInstruments(data.getFinancialInstruments?.data || []);
    } catch (error) {
      console.error('Error fetching instruments:', error);
    }
  }, 1200);

  const handleSearchPlatformPortfolio = async values => {
    try {
      const {data} = await getPlatformPortfolio({
        variables: {
          input: {
            instrumentKey,
            status: values.status,
            instrumentId: values.instrumentId,
            stockExchange: values.stockExchange || null,
          },
        },
      });
      if (data?.getPlatformPortfolio?.data?.length > 0) {
        setPlatformPortfolio(data?.getPlatformPortfolio?.data || []);
        setCurrentStep(2);
      } else {
        toast.warning('No Result Found');
      }
    } catch (error) {
      console.error('Error fetching platform portfolio:', error);
    }
  };

  const handleBackToSearch = () => {
    setCurrentStep(1);
  };

  const handleDownloadCSV = () => {
    if (!platformPortfolio.length) {
      toast.error('No data to download');
      return;
    }

    const headers = ['Name', 'Symbol', 'Type', 'Units', 'Instrument ID'];
    const csvData = platformPortfolio.map(item => [
      item?.name,
      item?.symbol,
      item?.type,
      item?.units,
      item?.instrumentId,
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `platform_portfolio_${new Date().toISOString().split('T')[0]}.csv`,
    );
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderForm = () => (
    <Form
      onFinish={handleSearchPlatformPortfolio}
      form={form}
      layout="vertical">
      <div className={styles.form}>
        <div className={styles.formHeader}>
          {/* The shell header already says what this screen is; this is the
              part that is not obvious — where the instrument comes from. */}
          <p className={styles.formDescription}>
            Bonds and mutual funds are picked from their asset class. Public
            equities, private equities and venture funds are searched directly.
          </p>
        </div>
        <div className={styles.formFields}>
          <Form.Item
            className={styles.largeWidth}
            rules={[{required: true, message: 'Please select a status'}]}
            name="status"
            label="Status(es)">
            <Select
              mode="multiple"
              allowClear
              onChange={selectedValues => {
                const specialStatuses = [
                  'SETTLED',
                  'FILLED',
                  'PARTIALLY_FILLED',
                ];
                const hasAnySpecialStatus = specialStatuses.some(status =>
                  selectedValues.includes(status),
                );

                if (hasAnySpecialStatus) {
                  const otherValues = selectedValues.filter(
                    value => !specialStatuses.includes(value),
                  );
                  const newValues = [...otherValues, ...specialStatuses];
                  form.setFieldValue('status', newValues);
                }
              }}>
              {stockOrderStatus?.map(data => (
                <Select.Option key={data.key}>{data.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            className={styles.largeWidth}
            name="stockExchange"
            label="Stock Exchange">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              loading={loadingExchanges}
              placeholder="All exchanges"
              options={(stockExchanges?.getAllStocksExchange ?? [])
                .filter(exchange => exchange?.id)
                .map(exchange => ({
                  value: exchange.id,
                  label: exchange.stockExchangeName,
                }))}
            />
          </Form.Item>
          <Form.Item
            className={styles.largeWidth}
            name="instrumentKey"
            label="Select Instrument">
            <Select
              placeholder="Public equity"
              allowClear
              options={instrumentKeyOptions(ORDER_INSTRUMENT_KEYS)}
            />
          </Form.Item>

          {instrumentKey && ['BOND', 'MUTUAL_FUND'].includes(instrumentKey) && (
            <Form.Item
              name="instrumentId"
              className={styles.largeWidth}
              label="Instrument based on Selected Asset">
              <Select
                showSearch
                onSearch={handleSearchInstrument}
                placeholder="Search Instrument"
                filterOption={false}
                loading={loadingInstruments}
                allowClear>
                {instruments.map(instrument => (
                  <Select.Option key={instrument?.id}>
                    {instrument?.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
          {PICKER_KEYS.includes(instrumentKey) && (
            <Form.Item
              name="instrumentId"
              className={styles.largeWidth}
              label={instrumentKeyLabel(instrumentKey)}>
              <FinancialInstrumentPicker targetKind={instrumentKey} />
            </Form.Item>
          )}
          <div className={styles.searchRow}>
            <SubmitButton
              disabled={loadingPlatformPortfolio}
              label={loadingPlatformPortfolio ? 'Searching…' : 'Search'}
            />
          </div>
        </div>
      </div>
    </Form>
  );

  const renderResults = () => (
    <div className={styles.tableContainer}>
      <div className={styles.tableHeader}>
        <div className={styles.tableHeaderLeft}>
          {/* The shell header already carries the way back to the search. */}
          <h2>
            {platformPortfolio.length} holding
            {platformPortfolio.length === 1 ? '' : 's'}
          </h2>
        </div>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleDownloadCSV}
          className={styles.downloadButton}>
          Download CSV
        </Button>
      </div>
      <div className={styles.portfolioTable}>
        <Table
          dataSource={platformPortfolio}
          columns={columns}
          loading={loadingPlatformPortfolio}
          pagination={false}
          className={styles.table}
        />
      </div>
    </div>
  );

  return (
    <ModalShell
      title="Platform portfolio"
      subtitle={
        currentStep === 1
          ? 'What the platform itself holds, by venue, instrument and order status.'
          : 'Holdings matching your search.'
      }
      onBack={currentStep === 2 ? handleBackToSearch : undefined}
      backLabel="Search again">
      {/* The class is the styling scope for everything below it, so it stays
          even though the shell now owns the padding and heading. */}
      <div className={styles.platformPortfolio}>
        {currentStep === 1 ? renderForm() : renderResults()}
      </div>
    </ModalShell>
  );
};

const columns = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
  },
  {
    title: 'Symbol',
    dataIndex: 'symbol',
    key: 'symbol',
  },
  {
    title: 'Instrument Type',
    dataIndex: 'type',
    key: 'type',
    render: (text, record) => <div>{record?.type?.replace('_', ' ')}</div>,
  },
  {
    title: 'Units',
    dataIndex: 'units',
    key: 'units',
  },
];
